import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { PageHero, Reveal, Eyebrow, EASE } from "@/components/bits";
import { INTEREST_OPTIONS, EMAIL } from "@/data/content";

const initialForm = { name: "", organisation: "", email: "", interest: "", message: "" };

const inputCls =
  "w-full bg-white border border-tnn-ink/12 rounded-xl px-4 py-3 text-sm text-tnn-ink placeholder:text-tnn-muted/60 focus:outline-none focus:border-tnn-purple focus:ring-2 focus:ring-tnn-purple/15 transition-[border-color,box-shadow] duration-200";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.interest) {
      toast.error("Please select what you are interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        testid="contact-hero"
        eyebrow="Contact / Partner With Us"
        title={
          <>
            Have a nutrition or healthcare problem <span className="text-tnn-purple">worth solving?</span>
          </>
        }
        lead="Let's turn it into something people can use."
      />

      <section className="bg-tnn-warm pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <Reveal>
                <Eyebrow>Start the Conversation</Eyebrow>
                <p className="mt-4 text-[15px] text-tnn-muted leading-relaxed">
                  Tell us what you're working on. We read every message and reply personally.
                </p>
                <div className="mt-8">
                  <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-tnn-purple">Prefer email?</p>
                  <a
                    href={`mailto:${EMAIL}`}
                    data-testid="contact-email-link"
                    className="group mt-2 inline-flex items-center gap-1.5 font-display font-semibold text-tnn-ink hover:text-tnn-purple transition-colors duration-200"
                  >
                    {EMAIL}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-3">
              <Reveal delay={0.08}>
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="bg-white border border-tnn-ink/8 rounded-3xl p-10"
                    data-testid="contact-confirmation"
                  >
                    <span className="w-11 h-11 rounded-full bg-tnn-purple flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </span>
                    <h2 className="mt-6 font-display text-2xl font-bold text-tnn-ink">Thank you.</h2>
                    <p className="mt-3 text-[15px] text-tnn-muted leading-relaxed">
                      We've received your message. Someone from TNN Africa will get back to you shortly.
                    </p>
                    <button
                      onClick={() => {
                        setForm(initialForm);
                        setSubmitted(false);
                      }}
                      data-testid="contact-send-another"
                      className="mt-8 text-xs font-bold tracking-[0.18em] uppercase text-tnn-purple hover:text-tnn-deep transition-colors duration-200"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <form
                    onSubmit={onSubmit}
                    data-testid="contact-form"
                    className="bg-white border border-tnn-ink/8 rounded-3xl p-8 lg:p-10"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="contact-name" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2">
                          Name *
                        </label>
                        <input
                          id="contact-name"
                          data-testid="contact-name-input"
                          required
                          value={form.name}
                          onChange={set("name")}
                          placeholder="Your full name"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-organisation" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2">
                          Organisation
                        </label>
                        <input
                          id="contact-organisation"
                          data-testid="contact-organisation-input"
                          value={form.organisation}
                          onChange={set("organisation")}
                          placeholder="Organisation or institution"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2">
                          Work Email *
                        </label>
                        <input
                          id="contact-email"
                          data-testid="contact-email-input"
                          type="email"
                          required
                          value={form.email}
                          onChange={set("email")}
                          placeholder="you@organisation.com"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-interest" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2">
                          What are you interested in? *
                        </label>
                        <select
                          id="contact-interest"
                          data-testid="contact-interest-select"
                          required
                          value={form.interest}
                          onChange={set("interest")}
                          className={`${inputCls} cursor-pointer ${form.interest ? "" : "text-tnn-muted/60"}`}
                        >
                          <option value="" disabled>
                            Select an area
                          </option>
                          {INTEREST_OPTIONS.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="contact-message" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2">
                          Message *
                        </label>
                        <textarea
                          id="contact-message"
                          data-testid="contact-message-input"
                          required
                          rows={5}
                          value={form.message}
                          onChange={set("message")}
                          placeholder="Tell us about the problem you are trying to solve"
                          className={`${inputCls} resize-none`}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      className="group mt-8 inline-flex items-center gap-2 bg-tnn-purple text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-colors duration-200 hover:bg-tnn-deep disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Sending
                        </>
                      ) : (
                        <>
                          Start the Conversation
                          <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
